<!-- Page Header -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <button class="btn btn-outline-secondary btn-sm"
              (click)="goBack()"
              style="margin-right: 10px;">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div class="header-icon">
        <i class="fa-solid fa-clock-rotate-left"></i>
      </div>
      <div class="header-text">
        <h1 class="page-title">Inventory Audit Logs</h1>
        <p class="page-description">Track inventory changes and modifications</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-export"
              (click)="exportLogs()"
              [disabled]="isLoading || isExporting">
        @if (isExporting) {
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          Exporting...
        } @else {
          <i class="fa-solid fa-download"></i>
          Export Logs
        }
      </button>
    </div>
  </div>
</div>

<!-- Filters Panel -->
<div class="filters-panel">
  <div class="filters-section">
    <div class="filter-group">
      <!-- Status Filter -->
      <div style="display: flex; flex-direction: column; gap: 4px;">
        <label style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em;">Status</label>
        <select class="form-select" style="min-width: 160px; height: 40px; font-size: 13px;"
                [(ngModel)]="selectedStatus"
                (change)="applyFilters()">
          <option value="">All Status</option>
          <option value="available">Available</option>
          <option value="sold_out">Sold Out</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <!-- Date Range Filter -->
      <div style="display: flex; flex-direction: column; gap: 4px;">
        <label style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em;">Date Range</label>
        <select class="form-select" style="min-width: 160px; height: 40px; font-size: 13px;"
                [(ngModel)]="dateRange"
                (change)="applyFilters()">
          <option value="">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      <!-- Search -->
      <div style="display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 200px;">
        <label style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em;">Search</label>
        <div class="search-box">
          <div class="search-input-wrapper">
            <i class="fa-solid fa-magnifying-glass search-icon"></i>
            <input type="text"
                   class="search-input"
                   placeholder="Search by name, category..."
                   [(ngModel)]="searchTerm"
                   (ngModelChange)="onSearchChange()"
                   style="height: 40px;">
          </div>
        </div>
      </div>

      <!-- Clear Button -->
      <div style="display: flex; flex-direction: column; gap: 4px;">
        <label style="font-size: 11px; color: transparent;">Clear</label>
        <button class="btn btn-outline-secondary" style="height: 40px;"
                (click)="clearFilters()">
          <i class="fa-solid fa-xmark"></i>
          Clear
        </button>
      </div>
    </div>
  </div>

  <!-- Result Stats -->
  @if (filteredSize > 0 || searchTerm || selectedStatus || dateRange) {
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fa-solid fa-list-check"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ filteredSize }}</div>
          <div class="stat-label">{{ (searchTerm || selectedStatus || dateRange) ? 'Filtered Results' : 'Total Entries' }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fa-solid fa-database"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ size }}</div>
          <div class="stat-label">Total Entries</div>
        </div>
      </div>
    </div>
  }
</div>

<!-- Data Table Panel -->
<div class="data-panel">
  <div class="panel-header">
    <div class="panel-title">
      <div class="title-icon">
        <i class="fa-solid fa-table-list"></i>
      </div>
      <div class="title-content">
        <div class="title-text">Audit Log Entries</div>
        <div class="title-subtitle">Detailed stock movement and change history</div>
      </div>
    </div>
  </div>

  <div class="panel-content">
    @if (isLoading) {
      <!-- Loading State -->
      <div class="loading-state">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="loading-text">
            <div class="loading-title">Loading Audit Logs</div>
            <div class="loading-subtitle">Please wait while we fetch the log data...</div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Table -->
      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 70px;"></th>
              <th>Item Details</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Expiry Date</th>
              <th>Cost Price</th>
              <th>Selling Price</th>
              <th>Total Value</th>
              <th>Status</th>
              <th style="width: 80px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for(log of filteredLogs; track log.id) {
              <tr>
                <td>
                  <img [src]="log.inventory.main_image || 'profile.png'"
                       width="44"
                       height="44"
                       alt="{{log.inventory.name}}"
                       style="border-radius: var(--radius-md); object-fit: cover; border: 1px solid var(--border);">
                </td>
                <td>
                  <div style="font-weight: 600; color: var(--text-primary); font-size: 13px;">{{log?.inventory?.name}}</div>
                  <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">ID: #{{log?.inventory?.id}}</div>
                </td>
                <td>
                  <span class="badge badge-soft-primary badge-dot">{{log?.inventory?.category?.name || 'Uncategorized'}}</span>
                </td>
                <td>
                  <span style="font-weight: 700; font-size: 14px; color: var(--text-primary);">{{log?.quantity}}</span>
                  <div style="font-size: 11px; color: var(--text-tertiary);">units</div>
                </td>
                <td>
                  <div style="font-size: 13px; font-weight: 500;">{{log?.expiry_date | date:'shortDate'}}</div>
                  <div style="font-size: 11px; margin-top: 2px;"
                       [style.color]="getExpiryStatus(log.expiry_date) === 'Expired' ? 'var(--danger-text)' :
                                      getExpiryStatus(log.expiry_date) === 'Expires today' ? 'var(--warning-text)' : 'var(--text-tertiary)'">
                    {{log?.expiry_date ? getExpiryStatus(log.expiry_date) : 'No expiry date'}}
                  </div>
                </td>
                <td>
                  <span style="font-weight: 500;">{{log?.cost_price | currency:'₦':'symbol':'1.2-2'}}</span>
                </td>
                <td>
                  <span style="font-weight: 500;">{{log?.inventory?.selling_price | currency:'₦':'symbol':'1.2-2'}}</span>
                </td>
                <td>
                  <span style="font-weight: 700; color: var(--accent);">{{log?.total_cost | currency:'₦':'symbol':'1.2-2'}}</span>
                </td>
                <td>
                  @if (log.is_expired) {
                    <span class="badge badge-soft-danger badge-dot">Expired</span>
                  } @else {
                    @if (log.sold_out) {
                      <span class="badge badge-soft-warning badge-dot">Sold Out</span>
                    } @else {
                      <span class="badge badge-soft-success badge-dot">Available</span>
                    }
                  }
                </td>
                <td>
                  <div ngbDropdown container="body" display="dynamic">
                    <button class="btn btn-sm btn-outline-secondary"
                            ngbDropdownToggle
                            [disabled]="isUpdating === log.id"
                            style="border: none; padding: 6px 10px;">
                      @if (isUpdating === log.id) {
                        <div class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      } @else {
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      }
                    </button>
                    <div ngbDropdownMenu>
                      <button ngbDropdownItem (click)="toggleStatus(log)">
                        <i class="fa-solid fa-right-left me-2" style="color: var(--info-text);"></i>
                        Switch to {{log.sold_out ? 'Available' : 'Sold Out'}}
                      </button>
                      <button ngbDropdownItem (click)="openUpdateModal(updateQuantityModal, log)">
                        <i class="fa-solid fa-pen-to-square me-2" style="color: var(--warning-text);"></i>
                        Update Quantity
                      </button>
                      <button ngbDropdownItem (click)="view(log.receipt)">
                        <i class="fa-solid fa-eye me-2" style="color: var(--accent);"></i>
                        View Receipt
                      </button>
                      <div class="dropdown-divider"></div>
                      <button ngbDropdownItem (click)="deleteLog(log)" style="color: var(--danger-text);">
                        <i class="fa-solid fa-trash-can me-2"></i>
                        Delete Log
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="10">
                  <div class="empty-state">
                    <div class="empty-icon">
                      <i class="fa-solid fa-clipboard-list" style="font-size: 40px;"></i>
                    </div>
                    <div class="empty-content">
                      <h5>No Audit Logs Found</h5>
                      <p>
                        @if (searchTerm || selectedStatus || dateRange) {
                          No logs match your current filters. Try adjusting your search criteria.
                        } @else {
                          There are no inventory audit logs at the moment.
                        }
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (size > pageSize && !isLoading && !searchTerm && !selectedStatus && !dateRange) {
        <div class="panel-footer">
          <div class="pagination-info">
            <div class="info-text">
              Showing {{((page - 1) * pageSize) + 1}} to {{Math.min(page * pageSize, size)}} of {{size}} entries
            </div>
          </div>
          <div class="pagination-controls">
            <ngb-pagination
              [collectionSize]="size"
              [(page)]="page"
              [pageSize]="pageSize"
              [maxSize]="5"
              [rotate]="true"
              [ellipses]="false"
              [boundaryLinks]="true"
              (pageChange)="refresh($event)"
              size="sm"
            />
          </div>
        </div>
      } @else if (filteredSize > 0 && (searchTerm || selectedStatus || dateRange)) {
        <div class="panel-footer" style="justify-content: center;">
          <div class="info-text">
            Showing {{filteredSize}} filtered result{{filteredSize !== 1 ? 's' : ''}} out of {{size}} total entries
          </div>
        </div>
      }
    }
  </div>
</div>

<!-- Update Quantity Modal -->
<ng-template #updateQuantityModal let-modal>
  <div class="modal-header">
    <div>
      <h4 class="modal-title" style="margin-bottom: 4px;">Update Inventory Quantity</h4>
      <p style="font-size: 13px; color: var(--text-tertiary); margin: 0;">Modify the quantity for {{selectedLog?.inventory?.name}}</p>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="form-group" style="margin-bottom: 20px;">
      <label for="quantity" class="form-label" style="font-weight: 600;">New Quantity</label>
      <div class="input-group">
        <button class="btn btn-outline-secondary" type="button" (click)="decreaseQuantity()" [disabled]="updatingQuantity <= 0">
          <i class="fa-solid fa-minus"></i>
        </button>
        <input type="number"
               class="form-control text-center"
               id="quantity"
               [(ngModel)]="updatingQuantity"
               min="0"
               placeholder="Enter quantity"
               style="font-weight: 600; font-size: 16px;">
        <button class="btn btn-outline-secondary" type="button" (click)="increaseQuantity()">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
      <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 8px;">
        Current quantity: {{selectedLog?.quantity}} units
      </div>
    </div>

    @if (selectedLog) {
      <div style="padding: 16px; background: var(--bg-muted); border-radius: var(--radius-md); border: 1px solid var(--border);">
        <div class="row g-3">
          <div class="col-6">
            <div style="font-size: 12px; color: var(--text-tertiary); margin-bottom: 4px;">Cost Price</div>
            <div style="font-weight: 600; color: var(--text-primary);">{{selectedLog.cost_price | currency:'₦':'symbol':'1.2-2'}}</div>
          </div>
          <div class="col-6">
            <div style="font-size: 12px; color: var(--text-tertiary); margin-bottom: 4px;">New Total Value</div>
            <div style="font-weight: 700; color: var(--accent);">{{(selectedLog.cost_price * updatingQuantity) | currency:'₦':'symbol':'1.2-2'}}</div>
          </div>
        </div>
      </div>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button"
            class="btn btn-primary"
            (click)="modal.close('save')"
            [disabled]="isUpdatingQuantity || updatingQuantity < 0">
      @if (isUpdatingQuantity) {
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        Updating...
      } @else {
        <i class="fa-solid fa-floppy-disk me-2"></i>
        Save Changes
      }
    </button>
  </div>
</ng-template>
